'use client';

import { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Archive,
  Save,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  useBaseSpecs,
  useCreateBaseSpec,
  useUpdateBaseSpec,
  useUpdateBaseSpecStatus,
  useDeleteBaseSpec,
  type BaseSpec,
} from '@/hooks/use-base-specs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface BaseSpecEditorProps {
  projectId: string;
  projectName?: string;
}

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  IN_REVIEW: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Clock },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-600', icon: Archive },
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['IN_REVIEW'],
  IN_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['ARCHIVED', 'DRAFT'],
  ARCHIVED: ['DRAFT'],
};

export function BaseSpecEditor({ projectId, projectName = 'Project' }: BaseSpecEditorProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingSpec, setEditingSpec] = useState<BaseSpec | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  const { toast } = useToast();
  const { data: baseSpecs, isLoading } = useBaseSpecs(projectId);
  const createMutation = useCreateBaseSpec(projectId);
  const updateMutation = useUpdateBaseSpec(projectId);
  const statusMutation = useUpdateBaseSpecStatus(projectId);
  const deleteMutation = useDeleteBaseSpec(projectId);

  const handleCreate = async (data: Partial<BaseSpec>) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Base Spec created', description: 'Base spec has been created successfully.' });
      setShowCreateDialog(false);
    } catch (error) {
      toast({ 
        title: 'Creation failed', 
        description: error instanceof Error ? error.message : 'Failed to create base spec',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (specId: string, data: Partial<BaseSpec>) => {
    try {
      await updateMutation.mutateAsync({ specId, data });
      toast({ title: 'Base Spec updated', description: 'Base spec has been updated successfully.' });
      setEditingSpec(null);
    } catch (error) {
      toast({ 
        title: 'Update failed', 
        description: error instanceof Error ? error.message : 'Failed to update base spec',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (specId: string, newStatus: BaseSpec['status'], readyForContract?: boolean) => {
    try {
      await statusMutation.mutateAsync({ specId, status: newStatus, readyForContract });
      toast({ title: 'Status updated', description: `Base spec status changed to ${newStatus}.` });
    } catch (error) {
      toast({ 
        title: 'Status update failed', 
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (specId: string) => {
    try {
      await deleteMutation.mutateAsync(specId);
      toast({ title: 'Base Spec deleted', description: 'Base spec has been deleted successfully.' });
      setShowDeleteConfirm(null);
    } catch (error) {
      toast({ 
        title: 'Deletion failed', 
        description: error instanceof Error ? error.message : 'Failed to delete base spec',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Base Specs</h1>
            <p className="text-sm text-gray-600 mt-1">Manage base specifications for {projectName}</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Base Spec
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!baseSpecs || baseSpecs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <FileText className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Base Specs</h3>
            <p className="text-gray-600 mb-6">Create your first base spec to get started.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Base Spec
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {baseSpecs.map((spec) => {
              const statusConfig = STATUS_CONFIG[spec.status];
              const StatusIcon = statusConfig.icon;
              const availableTransitions = VALID_TRANSITIONS[spec.status] || [];
              
              return (
                <Card key={spec.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{spec.title}</CardTitle>
                          <Badge className={cn(statusConfig.color, 'flex items-center gap-1')}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                          {spec.readyForContract && (
                            <Badge className="bg-purple-100 text-purple-800">Ready for Contract</Badge>
                          )}
                        </div>
                        <CardDescription>Version {spec.version}</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingSpec(spec)}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(spec.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {spec.vision && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-1">Vision</h4>
                          <p className="text-sm text-gray-600">{spec.vision}</p>
                        </div>
                      )}
                      
                      {availableTransitions.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t">
                          <span className="text-sm text-gray-600">Change status:</span>
                          {availableTransitions.map((transition) => (
                            <Button
                              key={transition}
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(spec.id, transition as BaseSpec['status'])}
                              disabled={statusMutation.isPending}
                            >
                              {STATUS_CONFIG[transition as keyof typeof STATUS_CONFIG].label}
                            </Button>
                          ))}
                          {spec.status === 'APPROVED' && !spec.readyForContract && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(spec.id, spec.status, true)}
                              disabled={statusMutation.isPending}
                            >
                              Mark Ready for Contract
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <BaseSpecDialog
        isOpen={showCreateDialog || editingSpec !== null}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingSpec(null);
        }}
        onSave={editingSpec 
          ? (data) => handleUpdate(editingSpec.id, data)
          : handleCreate
        }
        spec={editingSpec}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Base Spec</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this base spec? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface BaseSpecDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<BaseSpec>) => Promise<void>;
  spec?: BaseSpec | null;
  isLoading?: boolean;
}

// Type definitions for structured JSON fields
type ScopeItem = { item: string; type: 'in_scope' | 'out_of_scope'; rationale?: string };
type SuccessCriterion = { metric: string; target: string; measurement?: string };
type UserStory = {
  id: string;
  title: string;
  asA: string;
  iWant: string;
  soThat: string;
  acceptanceCriteria: string[];
  exceptions?: string[];
  priority?: 'must' | 'should' | 'could' | 'wont';
  linkedPersonaId?: string;
  linkedUseCaseId?: string;
};
type TraceabilityMatrixItem = {
  requirement: string;
  source: string;
  verification: string;
};

function BaseSpecDialog({ isOpen, onClose, onSave, spec, isLoading }: BaseSpecDialogProps) {
  // Helper to safely parse JSON fields from backend (may be Record or array)
  const parseJsonField = <T,>(value: Record<string, unknown> | null | undefined, defaultValue: T[]): T[] => {
    if (!value) return defaultValue;
    if (Array.isArray(value)) return value as T[];
    // If it's a Record, try to extract array from it or return empty
    return defaultValue;
  };

  const [formData, setFormData] = useState<Partial<BaseSpec>>({
    title: spec?.title || '',
    version: spec?.version || '1.0.0',
    vision: spec?.vision || '',
    problemStatement: spec?.problemStatement || '',
    scope: spec?.scope || null,
    assumptions: spec?.assumptions || null,
    constraints: spec?.constraints || null,
    successCriteria: spec?.successCriteria || null,
    userStories: spec?.userStories || null,
    traceabilityMatrix: spec?.traceabilityMatrix || null,
  });

  // Local state for structured fields (arrays)
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>(() => 
    parseJsonField<ScopeItem>(spec?.scope as Record<string, unknown> | null, [])
  );
  const [assumptions, setAssumptions] = useState<string[]>(() => 
    parseJsonField<string>(spec?.assumptions as Record<string, unknown> | null, [])
  );
  const [constraints, setConstraints] = useState<string[]>(() => 
    parseJsonField<string>(spec?.constraints as Record<string, unknown> | null, [])
  );
  const [successCriteria, setSuccessCriteria] = useState<SuccessCriterion[]>(() => 
    parseJsonField<SuccessCriterion>(spec?.successCriteria as Record<string, unknown> | null, [])
  );
  const [userStories, setUserStories] = useState<UserStory[]>(() => 
    parseJsonField<UserStory>(spec?.userStories as Record<string, unknown> | null, [])
  );
  const [traceabilityMatrix, setTraceabilityMatrix] = useState<TraceabilityMatrixItem[]>(() => 
    parseJsonField<TraceabilityMatrixItem>(spec?.traceabilityMatrix as Record<string, unknown> | null, [])
  );

  // Validation errors state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Title is required
    if (!formData.title || formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }

    // Validate scope items
    scopeItems.forEach((item, index) => {
      if (!item.item || item.item.trim().length === 0) {
        newErrors[`scope-${index}`] = 'Scope item description is required';
      }
    });

    // Validate success criteria
    successCriteria.forEach((criterion, index) => {
      if (!criterion.metric || criterion.metric.trim().length === 0) {
        newErrors[`success-metric-${index}`] = 'Metric is required';
      }
      if (!criterion.target || criterion.target.trim().length === 0) {
        newErrors[`success-target-${index}`] = 'Target is required';
      }
    });

    // Validate user stories
    userStories.forEach((story, index) => {
      if (!story.title || story.title.trim().length === 0) {
        newErrors[`story-title-${index}`] = 'Story title is required';
      }
      if (!story.asA || story.asA.trim().length === 0) {
        newErrors[`story-asA-${index}`] = 'As a [persona] is required';
      }
      if (!story.iWant || story.iWant.trim().length === 0) {
        newErrors[`story-iWant-${index}`] = 'I want [capability] is required';
      }
      if (!story.soThat || story.soThat.trim().length === 0) {
        newErrors[`story-soThat-${index}`] = 'So that [benefit] is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset state when spec changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      const parsedScope = parseJsonField<ScopeItem>(spec?.scope as Record<string, unknown> | null, []);
      const parsedAssumptions = parseJsonField<string>(spec?.assumptions as Record<string, unknown> | null, []);
      const parsedConstraints = parseJsonField<string>(spec?.constraints as Record<string, unknown> | null, []);
      const parsedSuccessCriteria = parseJsonField<SuccessCriterion>(spec?.successCriteria as Record<string, unknown> | null, []);
      const parsedUserStories = parseJsonField<UserStory>(spec?.userStories as Record<string, unknown> | null, []);
      const parsedTraceabilityMatrix = parseJsonField<TraceabilityMatrixItem>(spec?.traceabilityMatrix as Record<string, unknown> | null, []);

      setFormData({
        title: spec?.title || '',
        version: spec?.version || '1.0.0',
        vision: spec?.vision || '',
        problemStatement: spec?.problemStatement || '',
        scope: spec?.scope || null,
        assumptions: spec?.assumptions || null,
        constraints: spec?.constraints || null,
        successCriteria: spec?.successCriteria || null,
        userStories: spec?.userStories || null,
        traceabilityMatrix: spec?.traceabilityMatrix || null,
      });
      setScopeItems(parsedScope);
      setAssumptions(parsedAssumptions);
      setConstraints(parsedConstraints);
      setSuccessCriteria(parsedSuccessCriteria);
      setUserStories(parsedUserStories);
      setTraceabilityMatrix(parsedTraceabilityMatrix);
      setErrors({}); // Clear errors when dialog opens
    }
  }, [isOpen, spec]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate before submitting
    if (!validateForm()) {
      return;
    }

    const dataToSave: Partial<BaseSpec> = {
      ...formData,
      scope: scopeItems.length > 0 ? scopeItems : null,
      assumptions: assumptions.length > 0 ? assumptions : null,
      constraints: constraints.length > 0 ? constraints : null,
      successCriteria: successCriteria.length > 0 ? successCriteria : null,
      userStories: userStories.length > 0 ? userStories : null,
      traceabilityMatrix: traceabilityMatrix.length > 0 ? traceabilityMatrix : null,
    };
    await onSave(dataToSave);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{spec ? 'Edit Base Spec' : 'Create Base Spec'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                // Clear error when user types
                if (errors.title) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.title;
                    return newErrors;
                  });
                }
              }}
              required
              className={cn("mt-1", errors.title && "border-red-500")}
            />
            {errors.title && (
              <p className="text-sm text-red-600 mt-1">{errors.title}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Version</label>
            <Input
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Vision</label>
            <Textarea
              value={formData.vision || ''}
              onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Problem Statement</label>
            <Textarea
              value={formData.problemStatement || ''}
              onChange={(e) => setFormData({ ...formData, problemStatement: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Scope Items Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Scope</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setScopeItems([...scopeItems, { item: '', type: 'in_scope' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {scopeItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No scope items. Click "Add Item" to add one.</p>
              ) : (
                scopeItems.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Scope item description"
                        value={item.item}
                        onChange={(e) => {
                          const updated = [...scopeItems];
                          updated[index] = { ...updated[index], item: e.target.value };
                          setScopeItems(updated);
                          // Clear error when user types
                          if (errors[`scope-${index}`]) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors[`scope-${index}`];
                              return newErrors;
                            });
                          }
                        }}
                        className={errors[`scope-${index}`] ? "border-red-500" : ""}
                      />
                      {errors[`scope-${index}`] && (
                        <p className="text-xs text-red-600 mt-1">{errors[`scope-${index}`]}</p>
                      )}
                      <div className="flex gap-2">
                        <Select
                          value={item.type}
                          onValueChange={(value: 'in_scope' | 'out_of_scope') => {
                            const updated = [...scopeItems];
                            updated[index] = { ...updated[index], type: value };
                            setScopeItems(updated);
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="in_scope">In Scope</SelectItem>
                            <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Rationale (optional)"
                          value={item.rationale || ''}
                          onChange={(e) => {
                            const updated = [...scopeItems];
                            updated[index] = { ...updated[index], rationale: e.target.value };
                            setScopeItems(updated);
                          }}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setScopeItems(scopeItems.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Assumptions Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Assumptions</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAssumptions([...assumptions, ''])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Assumption
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {assumptions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No assumptions. Click "Add Assumption" to add one.</p>
              ) : (
                assumptions.map((assumption, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      placeholder="Enter assumption"
                      value={assumption}
                      onChange={(e) => {
                        const updated = [...assumptions];
                        updated[index] = e.target.value;
                        setAssumptions(updated);
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAssumptions(assumptions.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Constraints Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Constraints</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConstraints([...constraints, ''])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Constraint
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {constraints.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No constraints. Click "Add Constraint" to add one.</p>
              ) : (
                constraints.map((constraint, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      placeholder="Enter constraint"
                      value={constraint}
                      onChange={(e) => {
                        const updated = [...constraints];
                        updated[index] = e.target.value;
                        setConstraints(updated);
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setConstraints(constraints.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Success Criteria Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Success Criteria</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSuccessCriteria([...successCriteria, { metric: '', target: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Criterion
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {successCriteria.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No success criteria. Click "Add Criterion" to add one.</p>
              ) : (
                successCriteria.map((criterion, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <div>
                        <Input
                          placeholder="Metric"
                          value={criterion.metric}
                          onChange={(e) => {
                            const updated = [...successCriteria];
                            updated[index] = { ...updated[index], metric: e.target.value };
                            setSuccessCriteria(updated);
                            if (errors[`success-metric-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`success-metric-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`success-metric-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`success-metric-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`success-metric-${index}`]}</p>
                        )}
                      </div>
                      <div>
                        <Input
                          placeholder="Target"
                          value={criterion.target}
                          onChange={(e) => {
                            const updated = [...successCriteria];
                            updated[index] = { ...updated[index], target: e.target.value };
                            setSuccessCriteria(updated);
                            if (errors[`success-target-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`success-target-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`success-target-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`success-target-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`success-target-${index}`]}</p>
                        )}
                      </div>
                      <Input
                        placeholder="Measurement (optional)"
                        value={criterion.measurement || ''}
                        onChange={(e) => {
                          const updated = [...successCriteria];
                          updated[index] = { ...updated[index], measurement: e.target.value };
                          setSuccessCriteria(updated);
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSuccessCriteria(successCriteria.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* User Stories Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">User Stories</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const newStory: UserStory = {
                    id: `story-${Date.now()}`,
                    title: '',
                    asA: '',
                    iWant: '',
                    soThat: '',
                    acceptanceCriteria: [],
                  };
                  setUserStories([...userStories, newStory]);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Story
              </Button>
            </div>
            <div className="space-y-3 border rounded-lg p-3 max-h-96 overflow-y-auto">
              {userStories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No user stories. Click "Add Story" to add one.</p>
              ) : (
                userStories.map((story, index) => (
                  <div key={story.id || index} className="p-3 border rounded bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 mr-2">
                        <Input
                          placeholder="Story title"
                          value={story.title}
                          onChange={(e) => {
                            const updated = [...userStories];
                            updated[index] = { ...updated[index], title: e.target.value };
                            setUserStories(updated);
                            if (errors[`story-title-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`story-title-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`story-title-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`story-title-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`story-title-${index}`]}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setUserStories(userStories.filter((_, i) => i !== index))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <Input
                          placeholder="As a [persona]"
                          value={story.asA}
                          onChange={(e) => {
                            const updated = [...userStories];
                            updated[index] = { ...updated[index], asA: e.target.value };
                            setUserStories(updated);
                            if (errors[`story-asA-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`story-asA-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`story-asA-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`story-asA-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`story-asA-${index}`]}</p>
                        )}
                      </div>
                      <div>
                        <Input
                          placeholder="I want [capability]"
                          value={story.iWant}
                          onChange={(e) => {
                            const updated = [...userStories];
                            updated[index] = { ...updated[index], iWant: e.target.value };
                            setUserStories(updated);
                            if (errors[`story-iWant-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`story-iWant-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`story-iWant-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`story-iWant-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`story-iWant-${index}`]}</p>
                        )}
                      </div>
                      <div>
                        <Input
                          placeholder="So that [benefit]"
                          value={story.soThat}
                          onChange={(e) => {
                            const updated = [...userStories];
                            updated[index] = { ...updated[index], soThat: e.target.value };
                            setUserStories(updated);
                            if (errors[`story-soThat-${index}`]) {
                              setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[`story-soThat-${index}`];
                                return newErrors;
                              });
                            }
                          }}
                          className={errors[`story-soThat-${index}`] ? "border-red-500" : ""}
                        />
                        {errors[`story-soThat-${index}`] && (
                          <p className="text-xs text-red-600 mt-1">{errors[`story-soThat-${index}`]}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600">Acceptance Criteria</label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = [...userStories];
                            updated[index] = {
                              ...updated[index],
                              acceptanceCriteria: [...(updated[index].acceptanceCriteria || []), ''],
                            };
                            setUserStories(updated);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {(story.acceptanceCriteria || []).map((criteria, critIndex) => (
                          <div key={critIndex} className="flex gap-1">
                            <Input
                              placeholder="Criterion"
                              value={criteria}
                              onChange={(e) => {
                                const updated = [...userStories];
                                const criteriaList = [...(updated[index].acceptanceCriteria || [])];
                                criteriaList[critIndex] = e.target.value;
                                updated[index] = { ...updated[index], acceptanceCriteria: criteriaList };
                                setUserStories(updated);
                              }}
                              className="flex-1 text-sm"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = [...userStories];
                                const criteriaList = (updated[index].acceptanceCriteria || []).filter((_, i) => i !== critIndex);
                                updated[index] = { ...updated[index], acceptanceCriteria: criteriaList };
                                setUserStories(updated);
                              }}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Select
                        value={story.priority || ''}
                        onValueChange={(value: 'must' | 'should' | 'could' | 'wont' | '') => {
                          const updated = [...userStories];
                          updated[index] = { ...updated[index], priority: value || undefined };
                          setUserStories(updated);
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="must">Must</SelectItem>
                          <SelectItem value="should">Should</SelectItem>
                          <SelectItem value="could">Could</SelectItem>
                          <SelectItem value="wont">Won't</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Traceability Matrix Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Traceability Matrix</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTraceabilityMatrix([...traceabilityMatrix, { requirement: '', source: '', verification: '' }])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Entry
              </Button>
            </div>
            <div className="space-y-2 border rounded-lg p-3 max-h-60 overflow-y-auto">
              {traceabilityMatrix.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No traceability entries. Click "Add Entry" to add one.</p>
              ) : (
                traceabilityMatrix.map((entry, index) => (
                  <div key={index} className="flex gap-2 items-start p-2 border rounded bg-gray-50">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Requirement"
                        value={entry.requirement}
                        onChange={(e) => {
                          const updated = [...traceabilityMatrix];
                          updated[index] = { ...updated[index], requirement: e.target.value };
                          setTraceabilityMatrix(updated);
                        }}
                      />
                      <Input
                        placeholder="Source"
                        value={entry.source}
                        onChange={(e) => {
                          const updated = [...traceabilityMatrix];
                          updated[index] = { ...updated[index], source: e.target.value };
                          setTraceabilityMatrix(updated);
                        }}
                      />
                      <Input
                        placeholder="Verification"
                        value={entry.verification}
                        onChange={(e) => {
                          const updated = [...traceabilityMatrix];
                          updated[index] = { ...updated[index], verification: e.target.value };
                          setTraceabilityMatrix(updated);
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setTraceabilityMatrix(traceabilityMatrix.filter((_, i) => i !== index))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
